/**
 * Anchor-string generation for e-signature placements.
 *
 * The platform used to require the originator to type white-on-white text into
 * the PDF by hand and re-type the same literal into the drawer; drift between
 * the two was undetectable and failed silently at signing time. We now generate
 * the anchor here and stamp it into the generated PDF ourselves
 * (`form-renderer/collectAnchorStamps.ts` → `fillPdf.ts`), so the two can no
 * longer disagree.
 *
 * Every string produced here reaches BMO verbatim as a `textAnchor`, so the
 * delimiter rules from spec §5.4 are load-bearing: `textAnchor` is always
 * square-bracket wrapped, and the brackets are part of the literal in the PDF.
 */

import type { SignatoryRole, SignatureDocument, SignatureField } from './templateTypes';

/** Bracket-wrapped signature anchor, e.g. `[SignatorySig0]`. */
export type SignatureAnchor = string;

/**
 * The advisor has no display name — the domain forbids one, because they are
 * always rendered as "Advisor" — so their anchors are named from this constant
 * rather than from a label that does not exist.
 */
export const ADVISOR_ANCHOR_NAME = 'Advisor';

/**
 * The signatory display name reduced to an anchor-safe token.
 *
 * Spec §5.4 restricts the signer `id` charset to `0-9 a-z A-Z _ -` and reserves
 * the dot as the `binding` separator (`{id.anchor}`). The anchor body is
 * composed into both `binding` and the date anchor's dotted path, so we are
 * stricter still and keep alphanumerics only — a `_` or `-` would survive, but
 * nothing about a display name needs them and every character we allow is one
 * more way to collide with a delimiter.
 *
 * Returns `''` for a name with no alphanumeric content; callers treat that as
 * "not nameable yet" rather than substituting a default, so the originator is
 * told to fix the name instead of getting an anchor they never chose.
 */
export function signatorySlug(label: string): string {
  return label.replace(/[^A-Za-z0-9]/g, '');
}

/**
 * The anchor for a signatory's `n`th signature **within one PDF**.
 *
 * `n` is 0-based and resets per signatory per document: uniqueness is only ever
 * required within a document (`WorkflowStep.SetOneSpanConfig` enforces exactly
 * that), so restarting in each PDF keeps the numbers meaningful — the third box
 * in a form reads as `Sig2` rather than inheriting a running total from a
 * document the reader isn't looking at.
 */
export function signatureAnchor(slug: string, index: number): SignatureAnchor {
  return `[${slug}Sig${index}]`;
}

/**
 * The anchor without its square brackets — what gets embedded in a `binding`
 * (`{applicant-1.SignatorySig0}`) and in a date anchor's dotted path.
 *
 * Mirrors `BmoDataBuilder.AnchorBody` on the server. Both exist because the
 * brackets delimit the PDF literal, not the path; keep them in agreement.
 */
export function anchorBody(anchor: SignatureAnchor): string {
  const trimmed = anchor.trim();
  return trimmed.length >= 2 && trimmed.startsWith('[') && trimmed.endsWith(']')
    ? trimmed.slice(1, -1)
    : trimmed;
}

/**
 * The companion date anchor for a signature, per spec §5.4:208-224 —
 * `[<signature anchor body>.label<i>.Date]`. One pair of brackets wraps the
 * whole composed value, which is why the signature's own brackets come off
 * first.
 *
 * `labelIndex` is 0-based and resets per `tagRole`, which is unique per signer
 * (equal to that signer's id) — so in practice it resets per signatory. The
 * server owns the authoritative counter when it builds the wire payload; this
 * is here so the browser can stamp matching white text into the PDF, and the
 * two MUST agree.
 */
export function dateAnchor(anchor: SignatureAnchor, labelIndex: number): SignatureAnchor {
  return `[${anchorBody(anchor)}.label${labelIndex}.Date]`;
}

/**
 * The signatory token a signature's anchor was built from — `[AdvisorSig1]` →
 * `Advisor`.
 *
 * Recovered from the anchor rather than re-derived from the role's display name
 * ON PURPOSE. `BmoDataBuilder` has to compose the identical input anchor
 * server-side, and a second `signatorySlug` implementation in C# could drift
 * from this one by a character — a divergence that fails silently, producing a
 * package where OneSpan cannot find the input box. Both sides already store the
 * signature's anchor and both already agree on it, so deriving from that string
 * is the one way the two cannot disagree. Mirrors
 * `BmoDataBuilder.AnchorSlugPrefix`.
 *
 * `assignAnchors` always ends a generated anchor with `Sig<n>`, so stripping
 * that suffix is exact. A legacy hand-typed anchor has no such suffix and is
 * used whole (`[SalesSignature]` → `SalesSignature`), which is arbitrary but
 * stable — and unreachable in practice, since a legacy field has no placement
 * to hang an input box off.
 */
function anchorSlugPrefix(anchor: SignatureAnchor): string {
  // `[0-9]` rather than `\d` to be the same pattern character for character as the
  // server's `AnchorSigSuffix`: .NET's `\d` also matches Unicode digits, and the two
  // must strip identically.
  return anchorBody(anchor).replace(/Sig[0-9]+$/, '');
}

/**
 * The name a signature's `n`th input box binds under — `AdvisorInput0`.
 *
 * This is the `<anchor>` half of the input's `binding` (`{advisor-1.AdvisorInput0}`),
 * which carries the input's own name and NOT the dotted path its `textAnchor`
 * uses. Numbered in its own series: `n` is 0-based and resets per signer per
 * document, so a signer's first input is `Input0` whether it hangs off their
 * first signature or their third.
 */
export function inputAnchorName(anchor: SignatureAnchor, index: number): string {
  return `${anchorSlugPrefix(anchor)}Input${index}`;
}

/**
 * The companion input anchor for a signature — `[AdvisorSig1.AdvisorInput0]`.
 *
 * Composed the same way a date anchor is: the signature's own brackets come off,
 * one pair wraps the whole path, and the result is both the literal stamped into
 * the PDF and the `textAnchor` BMO is told to find.
 */
export function inputAnchor(anchor: SignatureAnchor, index: number): SignatureAnchor {
  return `[${anchorBody(anchor)}.${inputAnchorName(anchor, index)}]`;
}

/** The anchor-name source for one role: their display name, or "Advisor". */
export function anchorNameForRole(role: Pick<SignatoryRole, 'type' | 'labelEn'>): string {
  return role.type === 'advisor' ? ADVISOR_ANCHOR_NAME : (role.labelEn ?? '');
}

/**
 * The anchor token each role's anchors are built from, keyed by role slug.
 *
 * Display names are NOT unique — two parties on one application are routinely
 * both "Subscriber", and forcing the originator to invent a distinction BMO's
 * form does not make was a rule about our anchors leaking into their wording.
 * Anchors still have to be unique (the domain refuses two identical
 * `textAnchor`s in one PDF, and OneSpan would otherwise be told to find one
 * literal for two different signers), so a token that is already taken is
 * numbered from its SECOND holder on: `Subscriber`, `Subscriber2`,
 * `Subscriber3`. The first holder keeps the bare name, which is what makes this
 * a no-op for every config authored while names were distinct.
 *
 * The advisor is named first so the reserved `Advisor` token always lands on
 * the advisor, even when an applicant has been typed with that name.
 *
 * A role whose name has no alphanumeric content maps to `''` — "not nameable
 * yet"; `assignAnchors` leaves such a field's existing anchor alone rather than
 * substituting a default.
 */
export function anchorNamesBySlug(
  roles: readonly Pick<SignatoryRole, 'slug' | 'type' | 'labelEn'>[],
): Map<string, string> {
  // Advisor-first, otherwise authoring order. `sort` is stable, so applicants
  // keep their card order and the numbering stays put across saves.
  const ordered = [...roles].sort(
    (a, b) => Number(b.type === 'advisor') - Number(a.type === 'advisor'),
  );

  const taken = new Set<string>();
  const names = new Map<string, string>();
  for (const role of ordered) {
    const base = signatorySlug(anchorNameForRole(role));
    if (base === '') {
      names.set(role.slug, '');
      continue;
    }
    // Re-checked against every token already handed out, not just against
    // `base`, so a third "Subscriber" standing next to a literal "Subscriber 2"
    // still lands somewhere free instead of colliding with it.
    let name = base;
    for (let n = 2; taken.has(name); n++) name = `${base}${n}`;
    taken.add(name);
    names.set(role.slug, name);
  }
  return names;
}

/**
 * Recompute every anchor in every document from the roles' current display
 * names, and return the documents with those anchors applied.
 *
 * Anchors are DERIVED, never stored authoritatively — which is what lets a
 * signatory be renamed without the config and the PDF drifting apart. The
 * platform stamps the white text at generation time, so a regenerated anchor
 * simply produces different (matching) text in the next document; there is no
 * already-tagged PDF to fall out of step with.
 *
 * Both writers — the e-signature drawer and the placement page — run every
 * config through here before saving, so neither can persist a stale anchor.
 * Idempotent: running it on already-correct documents changes nothing.
 *
 * Numbering is 0-based and resets per signatory PER DOCUMENT. Uniqueness is only
 * ever required within a document (the domain enforces exactly that), so
 * restarting in each PDF keeps the number meaningful — the second box in a form
 * reads as `Sig1` rather than inheriting a running total from a document the
 * reader is not looking at.
 *
 * Two signatories may share a display name; `anchorNamesBySlug` numbers the
 * second one's TOKEN apart (`Subscriber` / `Subscriber2`) so their anchors still
 * differ. That is the only thing standing between a shared name and a duplicate
 * anchor, so the two functions are used as a pair — never slug a display name
 * into an anchor directly.
 */
export function assignAnchors(
  roles: readonly SignatoryRole[],
  documents: readonly SignatureDocument[],
): SignatureDocument[] {
  const nameBySlug = anchorNamesBySlug(roles);

  return documents.map((doc) => {
    const indexes = anchorIndexes(doc.fields);
    return {
      ...doc,
      fields: doc.fields.map((field, i) => {
        const slug = nameBySlug.get(field.role) ?? '';
        // A field whose role has been deleted, or whose name has no alphanumeric
        // content, has nothing to build an anchor from. Keeping the existing one
        // beats minting `[Sig0]` for every such field — that would collide, and
        // the domain would refuse the whole save with an error naming a rule the
        // originator never broke.
        if (slug === '') return field;

        return { ...field, textAnchor: signatureAnchor(slug, indexes[i]!) };
      }),
    };
  });
}

/**
 * The ordinal each field's anchor is numbered from, parallel to `fields`.
 *
 * This is the counter `assignAnchors` uses — 0-based, per signatory, restarting
 * in each document — pulled out so the placement page can label a box with the
 * same number the anchor carries without re-deriving it and drifting. The
 * badge's "· 2" and the PDF's `[SignatorySig1]` are then the same fact.
 *
 * Counted over EVERY field in the document, including ones with no placement in
 * the language currently on screen: a signature keeps one number across both
 * PDFs, which is the whole point of showing it.
 *
 * Every field is a signature — a date label and an input box are companions of
 * one (`enDatePlacements` / `enInputPlacements`), not entries of their own — so
 * this is one counter per signatory and nothing else can disturb it.
 */
export function anchorIndexes(fields: readonly SignatureField[]): number[] {
  const countByRole = new Map<string, number>();
  return fields.map((field) => {
    const index = countByRole.get(field.role) ?? 0;
    countByRole.set(field.role, index + 1);
    return index;
  });
}
